import { errorMessageFrom } from "../../../../../../../utils/formErrors";
import React, { useState } from "react";
import {
  Box, Stack, Avatar, IconButton, Menu, MenuItem, ListItemIcon, CircularProgress,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import MailIcon from "@mui/icons-material/MailOutline";
import PhoneIcon from "@mui/icons-material/PhoneOutlined";
import BadgeIcon from "@mui/icons-material/BadgeOutlined";
import ApartmentIcon from "@mui/icons-material/ApartmentOutlined";
import LogoutIcon from "@mui/icons-material/LogoutOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import { useNavigate } from "react-router-dom";
import BtnSecondary from "../../../../../../../component/SecondaryButton";
import OffboardDialog from "../../../../../../../component/OffboardDialog";
import LetterDialog from "../../../../../../../component/letters/LetterDialog";
import Modal, { ModalHead, ModalBody, ModalFoot } from "../../../../../../../component/Modal";
import BtnPrimary from "../../../../../../../component/PrimaryButton";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AssignRolesDialog from "../../../../../../../component/AssignRolesDialog";
import { getOrgDepartments, getActiveLegalUnit } from "../../../../../../../services/globalData";
import { useNotify } from "../../../../../../../component/NotificationProvider";
import {
  sendEmployeeOnboardingEmail, setEmployeeActive, removeEmployeeFromLegalUnit,
} from "../../../../../../../services/employee";
import { tokens, displayFont, radius } from "../../../../../../../theme";

const statusOf = (e) => {
  if (e?.isActive === false) return "Inactive";
  if (!e?.activated) return "Invited";          // invited, no login yet
  if (!e?.hasBeenOnboarded) return "Activated"; // logged in, profile incomplete
  return "Active";
};
const STATUS_STYLE = {
  Active: { color: tokens.successText, bg: tokens.successTint },
  Activated: { color: tokens.accent, bg: `${tokens.accentTint}` },
  Invited: { color: tokens.warningText, bg: tokens.warningTint },
  Inactive: { color: tokens.textMuted, bg: tokens.surfaceSubtle },
};

const Fact = ({ icon, value }) =>
  value ? (
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ color: tokens.textMuted, fontSize: "0.8125rem" }}>
      <Box sx={{ display: "flex", color: tokens.textMuted }}>{icon}</Box>
      <Box sx={{ color: tokens.text }}>{value}</Box>
    </Stack>
  ) : null;

export default function ProfileHeader({ employee, employeeId, onChanged }) {
  const navigate = useNavigate();
  const notify = useNotify();
  const [anchor, setAnchor] = useState(null);
  const [offboardOpen, setOffboardOpen] = useState(false);
  // Which letter is being previewed, or null. One piece of state for all
  // three: only one can be open, and the dialog differs only by type.
  const [letterType, setLetterType] = useState(null);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [rolesOpen, setRolesOpen] = useState(false);

  if (!employee) return null;

  const status = statusOf(employee);
  const st = STATUS_STYLE[status];
  const fullName = `${employee.fname || ""} ${employee.lname || ""}`.trim();
  const initials = `${employee.fname?.[0] || ""}${employee.lname?.[0] || ""}`.toUpperCase();
  const deptName = (getOrgDepartments() || []).find((d) => d?._id === employee.department)?.name;

  const close = () => setAnchor(null);

  const resend = async () => {
    close();
    try {
      const res = await sendEmployeeOnboardingEmail(employeeId);
      if (res?.success !== false) notify.success("Invite resent");
      else notify.error(errorMessageFrom(res, "Could not resend invite"));
    } catch (e) { notify.error(e?.message || "Could not resend invite"); }
  };

  const reactivate = async () => {
    close();
    try {
      const res = await setEmployeeActive(employeeId, true);
      if (res?.success !== false) { notify.success("Employee reactivated"); onChanged?.(); }
      else notify.error(errorMessageFrom(res, "Could not reactivate"));
    } catch (e) { notify.error(e?.message || "Could not reactivate"); }
  };

  const activeEntity = getActiveLegalUnit();
  const inActiveEntity = (employee.legalUnit || []).some((id) => id === activeEntity?._id);
  // Confirmed, because it was not. One click removed somebody from the entity
  // with no warning and no explanation of where they went — which is the
  // Unassigned bucket: still an employee, still active, in no company. Easy to
  // do by accident from a menu, and invisible afterwards unless you know that
  // bucket exists.
  const removeFromEntity = async () => {
    setRemoveBusy(true);
    try {
      const res = await removeEmployeeFromLegalUnit(activeEntity?._id, employeeId);
      if (res?.success !== false) {
        notify.success(`Removed from ${activeEntity?.name} — now unassigned`);
        setRemoveOpen(false);
        onChanged?.();
      } else notify.error(errorMessageFrom(res, "Could not remove from entity"));
    } catch (e) {
      notify.error(e?.message || "Could not remove from entity");
    } finally {
      setRemoveBusy(false);
    }
  };

  return (
    <Box sx={{ background: tokens.card, border: `1px solid ${tokens.border}`, borderRadius: `${radius.card}px`, p: "1.5rem", mb: "1.5rem" }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={2}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
          {/* Plum, not marigold. Marigold is reserved for the one action on a screen
              that cannot be undone; a person's initials are not an action. */}
          <Avatar
            src={employee.profileImage || undefined}
            sx={{
              width: "4.375rem",
              height: "4.375rem",
              bgcolor: tokens.primary,
              color: tokens.card,
              fontFamily: displayFont,
              fontSize: "1.375rem",
              fontWeight: 700,
            }}
          >
            {initials}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Box sx={{ fontFamily: displayFont, fontSize: "1.5rem", fontWeight: 700, color: tokens.text, letterSpacing: "-0.01em" }}>{fullName || "Employee"}</Box>
              {/* The product's badge, not a local box: a 6px rectangle read as a
                  tag rather than a status, and differed from the same status
                  everywhere else. */}
              <Box sx={{ fontSize: "0.75rem", fontWeight: 600, color: st.color, background: st.bg, borderRadius: "999px", px: "11px", py: "4px" }}>{status}</Box>
            </Stack>
            <Box sx={{ fontSize: "0.9375rem", color: tokens.textMuted, mt: "0.25rem" }}>
              {employee.designation || "—"}{deptName ? ` · ${deptName}` : ""}
            </Box>
            <Stack direction="row" spacing={2} sx={{ mt: "0.75rem", flexWrap: "wrap", rowGap: "0.5rem" }}>
              <Fact icon={<BadgeIcon sx={{ fontSize: "1rem" }} />} value={employee.aliasId} />
              <Fact icon={<MailIcon sx={{ fontSize: "1rem" }} />} value={employee.email} />
              <Fact icon={<PhoneIcon sx={{ fontSize: "1rem" }} />} value={employee.phone} />
              {deptName && <Fact icon={<ApartmentIcon sx={{ fontSize: "1rem" }} />} value={deptName} />}
            </Stack>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="flex-start">
          <BtnSecondary onClick={() => navigate(`/app/employee-details/${employeeId}/profile`)}
            sx={{ height: "2.5rem", px: "1rem", borderRadius: "8px" }}>
            <EditOutlinedIcon sx={{ fontSize: "1.1rem", mr: 0.5 }} /> Edit
          </BtnSecondary>
          <IconButton aria-label="More actions" onClick={(e) => setAnchor(e.currentTarget)} sx={{ border: `1px solid ${tokens.border}`, borderRadius: "8px", color: tokens.textMuted }}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { borderRadius: "10px", minWidth: "12rem" } } }}>
        {status === "Invited" && (
          <MenuItem onClick={resend} sx={{ fontSize: "0.875rem" }}>
            <ListItemIcon sx={{ minWidth: "2rem", color: tokens.textMuted }}><MailOutlineIcon fontSize="small" /></ListItemIcon>
            Resend invite
          </MenuItem>
        )}
        <MenuItem onClick={() => { close(); setRolesOpen(true); }} sx={{ fontSize: "0.875rem" }}>
          <ListItemIcon sx={{ minWidth: "2rem", color: tokens.textMuted }}><AdminPanelSettingsOutlinedIcon fontSize="small" /></ListItemIcon>
          Manage roles & access
        </MenuItem>
        {inActiveEntity && activeEntity?.name && (
          <MenuItem onClick={() => { close(); setRemoveOpen(true); }} sx={{ fontSize: "0.875rem" }}>
            <ListItemIcon sx={{ minWidth: "2rem", color: tokens.textMuted }}><LogoutIcon fontSize="small" /></ListItemIcon>
            Remove from {activeEntity.name}
          </MenuItem>
        )}
        {/* Letters, offered at the point in the lifecycle each belongs to
            rather than all at once. An offer letter for somebody who has not
            started; a relieving letter and a settlement for somebody who has
            left. Showing all three always would invite issuing a relieving
            letter to a current employee. */}
        <MenuItem onClick={() => { close(); setLetterType("offer"); }} sx={{ fontSize: "0.875rem" }}>
          <ListItemIcon sx={{ minWidth: "2rem", color: tokens.textMuted }}><DescriptionOutlinedIcon fontSize="small" /></ListItemIcon>
          Offer letter
        </MenuItem>
        {(employee?.lastWorkingDay || status === "Inactive") && (
          <MenuItem onClick={() => { close(); setLetterType("relieving"); }} sx={{ fontSize: "0.875rem" }}>
            <ListItemIcon sx={{ minWidth: "2rem", color: tokens.textMuted }}><DescriptionOutlinedIcon fontSize="small" /></ListItemIcon>
            Relieving letter
          </MenuItem>
        )}
        {(employee?.lastWorkingDay || status === "Inactive") && (
          <MenuItem onClick={() => { close(); setLetterType("settlement"); }} sx={{ fontSize: "0.875rem" }}>
            <ListItemIcon sx={{ minWidth: "2rem", color: tokens.textMuted }}><DescriptionOutlinedIcon fontSize="small" /></ListItemIcon>
            Settlement letter
          </MenuItem>
        )}
        {status === "Inactive" ? (
          <MenuItem onClick={reactivate} sx={{ fontSize: "0.875rem", color: tokens.successText }}>
            <ListItemIcon sx={{ minWidth: "2rem", color: tokens.successText }}><CheckCircleOutlineIcon fontSize="small" /></ListItemIcon>
            Reactivate
          </MenuItem>
        ) : (
          <MenuItem onClick={() => { close(); setOffboardOpen(true); }} sx={{ fontSize: "0.875rem", color: tokens.danger }}>
            <ListItemIcon sx={{ minWidth: "2rem", color: tokens.danger }}><BlockIcon fontSize="small" /></ListItemIcon>
            Offboard
          </MenuItem>
        )}
      </Menu>

      <Modal open={removeOpen} onClose={() => setRemoveOpen(false)} width="28rem">
        <ModalHead
          title={`Remove from ${activeEntity?.name || "this entity"}?`}
          subtitle={`${employee?.fname || ""} ${employee?.lname || ""}`.trim()}
          onClose={() => setRemoveOpen(false)}
        />
        <ModalBody>
          <Box sx={{ fontSize: "0.875rem", color: tokens.text, lineHeight: 1.7 }}>
            They stay an employee of this organisation and keep their account —
            this only ends their membership of {activeEntity?.name || "this entity"}.
          </Box>
          {/* Where they go, said plainly. Without this the person vanishes from
              the list you are looking at and reappears nowhere obvious. */}
          <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted, lineHeight: 1.7, mt: "0.75rem" }}>
            You will find them under <b>Unassigned</b> in All Employees, where they
            can be added to another entity. They will not appear in this entity's
            directory, payroll or attendance until they are.
          </Box>
          <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted, lineHeight: 1.7, mt: "0.75rem" }}>
            This is not offboarding. To record someone leaving the company, use
            Offboard instead.
          </Box>
        </ModalBody>
        <ModalFoot note="Recorded against their entity membership.">
          <BtnSecondary onClick={() => setRemoveOpen(false)} sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
          <BtnPrimary onClick={removeFromEntity} disabled={removeBusy}
            sx={{ borderRadius: "8px", background: tokens.danger, "&:hover": { background: tokens.danger } }}>
            {removeBusy ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Remove"}
          </BtnPrimary>
        </ModalFoot>
      </Modal>

      <LetterDialog
        employee={employee}
        type={letterType}
        open={Boolean(letterType)}
        onClose={() => setLetterType(null)}
      />

      <OffboardDialog
        open={offboardOpen}
        employee={employee}
        onClose={() => setOffboardOpen(false)}
        onDone={() => onChanged?.()}
      />
      <AssignRolesDialog
        open={rolesOpen}
        employee={employee}
        onClose={() => setRolesOpen(false)}
        onDone={() => onChanged?.()}
      />
    </Box>
  );
}
